/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {
  useCallback,
  useMemo
} from 'react';
import {
  useQuery,
  useQueryClient
} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {getAccountMessageEndpoint} from '@ion/core/src/services/Connect';
import {MessageEndpoint} from '@ion/core/src/types/entities';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, DurationResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseAccountMessageEndpointResult = UseDefaultQueryResult & {
  messageEndpoint: MessageEndpoint;
  refresh: () => void;
};

interface UseAccountMessageEndpointParams {
  accountId: string;
  enabled?: boolean;
}

const getMessageEndpoint = async(accountId: string): Promise<MessageEndpoint> => {
  try {
    const messageEndpoint = await getAccountMessageEndpoint(accountId);
    return messageEndpoint;
  } catch (err) {
    error(err);
  }
};

/**
 * React Query hook responsible for fetching an account's message endpoint.
 */
export const useAccountMessageEndpoint = ({
  accountId,
  enabled = true
}: UseAccountMessageEndpointParams): UseAccountMessageEndpointResult => {
  const queryClient = useQueryClient();

  const key = useMemo(() => (
    createKey(QueryKey.MESSAGE_ENDPOINT, accountId)
  ), [accountId]);

  const query = useQuery(key, async() => {
    const results = await getMessageEndpoint(accountId);
    return results;
  }, {
    ...getStaleTime(5) as DurationResult,
    enabled
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries(key);
  }, [key]);

  return createQueryResult(query, {
    messageEndpoint : query.data,
    refresh
  }) as UseAccountMessageEndpointResult;
};

export default useAccountMessageEndpoint;
